import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable, throwError, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { SanctumService } from 'src/app/services/sanctum.service';
import { AuthHeaderService } from 'src/app/services/auth-header.service';
import { switchMap } from 'rxjs/operators';
import { from } from 'rxjs';
import { RevalidatedHttpService, RevalidatedResource } from 'src/app/core/http-cache/revalidated-http.service';
// Tipagem mínima
export interface Paging {
  cursors?: { before?: string; after?: string };
  next?: string;
  previous?: string;
}

export interface FacebookPage {
  id: string;
  name: string;
  access_token?: string;
  picture?: string;
  followers_count?: number;
}

export interface FacebookPageEdge {
  data: FacebookPage[];
  paging: {
    cursors: {
      before?: string;
      after?: string;
    };
  };
}

export interface InstagramBusinessAccount {
  id: string;
  username?: string;
  name?: string;
  picture?: string;
  label?: string;
  media_count?: number;
  followers_count?: number;
  follows_count?: number;
}

export interface GraphEdgeResponse<T> {
  data: T[];
  paging?: {
    cursors: { before?: string; after?: string };
  };
}

export interface HashtagEdgeResponse<T> extends GraphEdgeResponse<T> {
  /** o backend está retornando exatamente essa chave */
  hastTag_id?: string;
}
@Injectable({
  providedIn: 'root'
})
export class InstagramGalleryService {

  private base = environment.production
    ? environment.serverUrl
    : environment.paymentGenetatorLinkURL;

  constructor(
    private http: HttpClient,
    private sanctum: SanctumService,
    private authHeaderService: AuthHeaderService,
    private revalidatedHttp: RevalidatedHttpService,
  ) {
  }

  private authHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('access_token')}`
    });
  }

  /** Listar mídias de usuário (endpoint /{ig_user_id}/media) */
  // instagram-gallery.service.ts

  /** Listar mídias do usuário (endpoint /{ig_user_id}/media) com filtro opcional por media_type[] */
  listUserMedia(
    igUserId: string,
    fbAccountId: string,
    limit: number = 10,
    after?: string,
    before?: string
  ): Observable<GraphEdgeResponse<any>> {
    const storeId = Number(localStorage.getItem('ss_SelectedStoreId'));
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${storeId}/instagram/user/${igUserId}/media`;

    let params = new HttpParams()
      .set('fbAccountId', fbAccountId)
      .set('limit', limit.toString());

    if (after) params = params.set('after', after);
    if (before) params = params.set('before', before);

    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.http.get<GraphEdgeResponse<any>>(url, {
          ...httpOptions,
          params
        })
      ),
      catchError(err => throwError(() => err))
    );
  }

  // Cache-first com ETag (src/app/core/http-cache).
  listUserMediaRevalidated(
    igUserId: string,
    fbAccountId: string,
    limit: number = 10,
    after?: string,
    before?: string
  ): RevalidatedResource<GraphEdgeResponse<any>> {
    const storeId = Number(localStorage.getItem('ss_SelectedStoreId'));
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${storeId}/instagram/user/${igUserId}/media`;

    let params = new HttpParams()
      .set('fbAccountId', fbAccountId)
      .set('limit', limit.toString());

    if (after) params = params.set('after', after);
    if (before) params = params.set('before', before);

    const cacheKey = `ig_user_media:${storeId}:${igUserId}:${fbAccountId}:${limit}:${after ?? ''}:${before ?? ''}`;

    return this.revalidatedHttp.getSignal<GraphEdgeResponse<any>>(
      cacheKey,
      url,
      this.authHeaderService.getHttpOptions(),
      { params }
    );
  }




  listFacebookPages(
    limit: number = 10,
    after?: string,
    facebookAccountId?: string
  ): Observable<FacebookPageEdge> {
    const selectedStoreId = Number(localStorage.getItem('ss_SelectedStoreId'));
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${selectedStoreId}/facebook/pages`;

    let params = new HttpParams().set('limit', limit.toString());
    if (after) params = params.set('after', after);
    if (facebookAccountId) params = params.set('fbAccountId', facebookAccountId);

    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.http.get<FacebookPageEdge>(url, {
          ...httpOptions,
          params
        })
      ),
      catchError(err => throwError(() => err))
    );
  }


  countSlicks(): Observable<any> {
    const id_loja = localStorage.getItem("ss_SelectedStoreId") || '';
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${id_loja}/countslicks`;

    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.http.get<any>(url, httpOptions)
      ),
      catchError(error => throwError(() => error))
    );
  }


  saveInstagramAccount(payload: {
    instagram_user_id: string;
    facebook_user_id: string;
  }): Observable<any> {
    const storeId = Number(localStorage.getItem('ss_SelectedStoreId'));
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${storeId}/instagram-account`;

    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(async httpOptions => {
        await this.sanctum.initSanctum();
        return this.http.post(url, payload, httpOptions);
      }),
      catchError(err => throwError(() => err))
    );
  }


  public getInstagramAccountForPage(
    pageId: string,
    facebookAccountId: string
  ): Observable<InstagramBusinessAccount | null> {
    const storeId = Number(localStorage.getItem('ss_SelectedStoreId'));
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${storeId}/facebook/pages/${pageId}/instagram_account`;

    const params = new HttpParams().set('fbAccountId', facebookAccountId);

    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() =>
        from(this.authHeaderService.getHttpOptions()).pipe(
          switchMap(httpOptions => {
            const finalOptions = { ...httpOptions, params };
            return this.http.get<InstagramBusinessAccount>(url, finalOptions);
          })
        )
      ),
      map(res => res ?? null),
      catchError(err => throwError(() => err))
    );
  }



  public getFacebookAccounts(): Observable<{
    accounts: Array<{ id: string; name: string; picture: string }>;
  }> {
    const storeId = localStorage.getItem('ss_SelectedStoreId') || '';
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${storeId}/facebook_accounts`;

    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.http.get<{ accounts: Array<{ id: string; name: string; picture: string }> }>(url, httpOptions)
      ),
      map(res => ({ accounts: res.accounts }))
    );
  }

  // Cache-first com ETag (src/app/core/http-cache).
  public getFacebookAccountsRevalidated(): RevalidatedResource<{
    accounts: Array<{ id: string; name: string; picture: string }>;
  }> {
    const storeId = localStorage.getItem('ss_SelectedStoreId') || '';
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${storeId}/facebook_accounts`;
    const cacheKey = `facebook_accounts:store:${storeId}`;

    return this.revalidatedHttp.getSignal(
      cacheKey,
      url,
      this.authHeaderService.getHttpOptions()
    );
  }


  // em instagram-gallery.service.ts
  listHashtagMedia(
    igUserId: string,
    tag: string,
    fbAccountId: string,
    limit?: number,
    after?: string
  ): Observable<HashtagEdgeResponse<any>> {
    const storeId = localStorage.getItem('ss_SelectedStoreId') || '';
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${storeId}/instagram/user/${igUserId}/hashtag/${tag}/media`;

    let params = new HttpParams()
      .set('limit', (limit ?? 3).toString())
      .set('fbAccountId', fbAccountId);

    if (after) {
      params = params.set('after', after);
    }

    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.http.get<HashtagEdgeResponse<any>>(url, {
          ...httpOptions,
          params
        })
      ),
      catchError(err => throwError(() => err))
    );
  }

  // Cache-first com ETag (src/app/core/http-cache) — evita bater na quota de
  // 30 hashtags/7 dias do Instagram (429) em revisitas repetidas da mesma busca.
  listHashtagMediaRevalidated(
    igUserId: string,
    tag: string,
    fbAccountId: string,
    limit?: number,
    after?: string
  ): RevalidatedResource<HashtagEdgeResponse<any>> {
    const storeId = localStorage.getItem('ss_SelectedStoreId') || '';
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${storeId}/instagram/user/${igUserId}/hashtag/${tag}/media`;

    let params = new HttpParams()
      .set('limit', (limit ?? 3).toString())
      .set('fbAccountId', fbAccountId);

    if (after) {
      params = params.set('after', after);
    }

    const cacheKey = `ig_hashtag_media:${storeId}:${igUserId}:${tag}:${fbAccountId}:${limit ?? 3}:${after ?? ''}`;

    return this.revalidatedHttp.getSignal<HashtagEdgeResponse<any>>(
      cacheKey,
      url,
      this.authHeaderService.getHttpOptions(),
      { params }
    );
  }


  listHashtagMediaRecent(
    igUserId: string,
    tag: string,
    fbAccountId: string,
    limit?: number,
    after?: string
  ): Observable<HashtagEdgeResponse<any>> {
    const storeId = localStorage.getItem('ss_SelectedStoreId') || '';
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${storeId}/instagram/user/${igUserId}/hashtag/${tag}/recent-media`;

    let params = new HttpParams()
      .set('limit', (limit ?? 10).toString())
      .set('fbAccountId', fbAccountId);

    if (after) {
      params = params.set('after', after);
    }

    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.http.get<HashtagEdgeResponse<any>>(url, {
          ...httpOptions,
          params
        })
      ),
      catchError(err => throwError(() => err))
    );
  }

  // Cache-first com ETag (src/app/core/http-cache) — evita bater na quota de
  // 30 hashtags/7 dias do Instagram (429) em revisitas repetidas da mesma busca.
  listHashtagMediaRecentRevalidated(
    igUserId: string,
    tag: string,
    fbAccountId: string,
    limit?: number,
    after?: string
  ): RevalidatedResource<HashtagEdgeResponse<any>> {
    const storeId = localStorage.getItem('ss_SelectedStoreId') || '';
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${storeId}/instagram/user/${igUserId}/hashtag/${tag}/recent-media`;

    let params = new HttpParams()
      .set('limit', (limit ?? 10).toString())
      .set('fbAccountId', fbAccountId);

    if (after) {
      params = params.set('after', after);
    }

    const cacheKey = `ig_hashtag_media_recent:${storeId}:${igUserId}:${tag}:${fbAccountId}:${limit ?? 10}:${after ?? ''}`;

    return this.revalidatedHttp.getSignal<HashtagEdgeResponse<any>>(
      cacheKey,
      url,
      this.authHeaderService.getHttpOptions(),
      { params }
    );
  }


  /**
   * Busca mídias em que o usuário foi **tagged**.
   */
  // antes:
  // listTaggedMedia(igUserId: string): Observable<any[]> { … }

  listTaggedMedia(
    igUserId: string,
    fbAccountId: string
  ): Observable<any[]> {
    const storeId = localStorage.getItem('ss_SelectedStoreId') || '';
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${storeId}/instagram/user/${igUserId}/tags/media`;

    const params = new HttpParams().set('fbAccountId', fbAccountId);

    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.http.get<any[]>(url, {
          ...httpOptions,
          params
        })
      ),
      catchError(err => throwError(() => err))
    );
  }

  // Cache-first com ETag (src/app/core/http-cache).
  listTaggedMediaRevalidated(
    igUserId: string,
    fbAccountId: string
  ): RevalidatedResource<any[]> {
    const storeId = localStorage.getItem('ss_SelectedStoreId') || '';
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${storeId}/instagram/user/${igUserId}/tags/media`;
    const params = new HttpParams().set('fbAccountId', fbAccountId);
    const cacheKey = `ig_tagged_media:${storeId}:${igUserId}:${fbAccountId}`;

    return this.revalidatedHttp.getSignal<any[]>(
      cacheKey,
      url,
      this.authHeaderService.getHttpOptions(),
      { params }
    );
  }





  ////////////////////
  // destroyInstagramAccount(uid: string): Observable<StoreAppsResponse> {
  //   const httpOptions = {
  //     headers: new HttpHeaders({
  //       'Content-Type': 'application/json',
  //       'Authorization': `Bearer ${this.getAccessToken()}`,
  //     }),
  //   };

  //   const selectedStoreId = localStorage.getItem('ss_SelectedStoreId');
  //   let store_id;
  //   if (selectedStoreId) {
  //     store_id = Number(selectedStoreId);
  //   }

  //   const url = environment.production
  //     ? `${environment.serverUrl}/admin/store/${store_id}/instagram_accounts/${uid}`
  //     : `${environment.paymentGenetatorLinkURL}/admin/store/${store_id}/instagram_accounts/${uid}`;

  //   return this.httpClient.delete<StoreAppsResponse>(url, httpOptions).pipe(
  //     catchError((error) => {
  //       console.error('Erro ao buscar apps:', error);
  //       return throwError(error);
  //     })
  //   );
  // }
  ////////////////////

  /** Listar stories de usuário (endpoint /{ig_user_id}/stories) */
  // antes: igUserId, limit?, after?, before?, mediaTypes?
  listUserStories(
    igUserId: string,
    fbAccountId: string,
    limit: number = 10,
    after?: string,
    before?: string,
    mediaTypes: string[] = []
  ): Observable<GraphEdgeResponse<any>> {
    const storeId = localStorage.getItem('ss_SelectedStoreId') || '';
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${storeId}/instagram/user/${igUserId}/stories`;

    let params = new HttpParams()
      .set('fbAccountId', fbAccountId)
      .set('limit', limit.toString());

    if (after) params = params.set('after', after);
    if (before) params = params.set('before', before);
    mediaTypes.forEach(t => params = params.append('media_type[]', t));

    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.http.get<GraphEdgeResponse<any>>(url, {
          ...httpOptions,
          params
        })
      ),
      catchError(err => throwError(() => err))
    );
  }

  // Cache-first com ETag (src/app/core/http-cache).
  listUserStoriesRevalidated(
    igUserId: string,
    fbAccountId: string,
    limit: number = 10,
    after?: string,
    before?: string,
    mediaTypes: string[] = []
  ): RevalidatedResource<GraphEdgeResponse<any>> {
    const storeId = localStorage.getItem('ss_SelectedStoreId') || '';
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${storeId}/instagram/user/${igUserId}/stories`;

    let params = new HttpParams()
      .set('fbAccountId', fbAccountId)
      .set('limit', limit.toString());

    if (after) params = params.set('after', after);
    if (before) params = params.set('before', before);
    mediaTypes.forEach(t => params = params.append('media_type[]', t));

    const cacheKey = `ig_user_stories:${storeId}:${igUserId}:${fbAccountId}:${limit}:${after ?? ''}:${before ?? ''}:${mediaTypes.join(',')}`;

    return this.revalidatedHttp.getSignal<GraphEdgeResponse<any>>(
      cacheKey,
      url,
      this.authHeaderService.getHttpOptions(),
      { params }
    );
  }


  /** Listar posts da própria Página do Facebook (uso: pages_read_engagement) */
  getFacebookPageFeed(
    pageId: string,
    fbAccountId: string,
    limit: number = 10,
    after?: string
  ): Observable<GraphEdgeResponse<any>> {
    const storeId = Number(localStorage.getItem('ss_SelectedStoreId'));
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${storeId}/facebook/pages/${pageId}/feed`;

    let params = new HttpParams()
      .set('fbAccountId', fbAccountId)
      .set('limit', limit.toString());
    if (after) params = params.set('after', after);

    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.http.get<GraphEdgeResponse<any>>(url, {
          ...httpOptions,
          params
        })
      ),
      catchError(err => throwError(() => err))
    );
  }


  /** Buscar detalhe de uma mídia (endpoint /{ig_media_id}) */
  getMediaDetail(storeId: number, mediaId: string): Observable<any> {
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${storeId}/instagram/media-detail/${mediaId}`;

    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.http.get<any>(url, httpOptions)
      ),
      catchError(err => throwError(() => err))
    );
  }


  /** Listar páginas gerenciadas (/me/accounts) */
  listManagedPages(
    storeId: number,
    limit?: number,
    after?: string,
    before?: string
  ): Observable<GraphEdgeResponse<any>> {
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${storeId}/facebook/me/accounts`;

    let params = new HttpParams();
    if (limit) params = params.set('limit', limit.toString());
    if (after) params = params.set('after', after);
    if (before) params = params.set('before', before);

    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.http.get<GraphEdgeResponse<any>>(url, { ...httpOptions, params })
      ),
      catchError(err => throwError(() => err))
    );
  }


  /** Listar contas Instagram conectadas a uma página (/{{page_id}}) */
  listInstagramFromPage(
    storeId: number,
    pageId: string,
    limit?: number,
    after?: string,
    before?: string
  ): Observable<GraphEdgeResponse<any>> {
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${storeId}/facebook/page/${pageId}/instagram-accounts`;

    let params = new HttpParams();
    if (limit) params = params.set('limit', limit.toString());
    if (after) params = params.set('after', after);
    if (before) params = params.set('before', before);

    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.http.get<GraphEdgeResponse<any>>(url, { ...httpOptions, params })
      ),
      catchError(err => throwError(() => err))
    );
  }

  /** “Me” do Facebook (/me) */
  getMe(storeId: number): Observable<any> {
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${storeId}/facebook/me`;

    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.http.get<any>(url, httpOptions)
      ),
      catchError(err => throwError(() => err))
    );
  }


  /** Debug de token (/debug_token) */
  debugToken(storeId: number): Observable<any> {
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${storeId}/facebook/debug-token`;

    return from(this.authHeaderService.getHttpOptions()).pipe(
      switchMap(httpOptions =>
        this.http.get<any>(url, httpOptions)
      ),
      catchError(err => throwError(() => err))
    );
  }


  // instagram.service.ts
  extractInstagramByUrlPublic(urlToExtract: string) {
    const storeId = localStorage.getItem('ss_SelectedStoreId') || '';
    const baseUrl = `${environment.apiBaseUrl}/api`;

    const url = `${baseUrl}/admin/store/${storeId}/instagram/extract`;
    const params: any = { url: urlToExtract };
  
    return from(this.sanctum.initSanctum()).pipe(
      switchMap(() => from(this.authHeaderService.getHttpOptions())),
      switchMap(opts => this.http.get<any>(url, { ...opts, params })),
      map(res => ({
        items: Array.isArray(res?.items) ? res.items : [],
        nextPageToken: null
      })),
      catchError(err => throwError(() => err)) // não converta em 200
    );
  }



  getAccessToken(): string {
    return localStorage.getItem("access_token") || '';
  }
}
